import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useUser } from '@clerk/react';
import { Button, ConsultationStepper } from '@/components';
import { useGlobalContext } from '@/store';
import { apiClient } from '@/api/client';
import { deletePatientSymptom } from '@/api/symptoms';
import { getUserCredits, consumeUserCredit } from '@/api/payments';

import { useRecaptcha } from '../useRecaptcha';
import { logTemporaryRemedies } from '@/utils/logTemporaryRemedies';


interface RemedyItem {
  remedy: string;
  score: number;
  matches: string[];
}

interface SymptomQuestionItem {
  symptom: string;
  question: string;
}

interface RemedyDetails {
  remedy: string;
  full_name: string;
  tailored_description: string;
  diagnosed_potency: string;
  administration_instructions: string;
}

export function Results() {
  const { patient, setToast, setErrorModal, setConfirmModal } = useGlobalContext();
  const navigate = useNavigate();
  const { user } = useUser();
  const { getRequestConfig, getErrorMessage } = useRecaptcha();
  const [results, setResults] = useState<RemedyItem[]>([]);
  const [symptoms, setSymptoms] = useState<SymptomQuestionItem[]>([]);
  const [validatedSymptoms, setValidatedSymptoms] = useState<SymptomQuestionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [symptomsLoaded, setSymptomsLoaded] = useState(false);
  const [validated, setValidated] = useState(false);
  const [deletingSymptom, setDeletingSymptom] = useState<string | null>(null);
  const [remedyDetails, setRemedyDetails] = useState<RemedyDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [patientStatus, setPatientStatus] = useState<any>(null);
  const [resultsFeedback, setResultsFeedback] = useState('');
  const [submittingResultsFeedback, setSubmittingResultsFeedback] = useState(false);

  const handleResultsFeedbackSubmit = async () => {
    if (!resultsFeedback.trim()) return;
    setSubmittingResultsFeedback(true);
    try {
      await apiClient.post('/patients/feedback', {
        email: patient.email,
        date_0: consultationDate,
        consultation_token: patient.consultationToken,
        feedback: resultsFeedback.trim(),
        name: patient.name,
      });
      setToast({ type: 'success', message: 'Feedback submitted! 2nd remedy unlocked.' });
      const res = await apiClient.get('/patients/by-email', {
        params: {
          email: patient.email,
          name: patient.name || undefined,
          consultation_token: patient.consultationToken || undefined,
        },
      });
      setPatientStatus(res.data);
    } catch (err) {
      /* eslint-disable-next-line no-console */
      console.error(err);
      setToast({ type: 'error', message: 'Failed to submit feedback.' });
    } finally {
      setSubmittingResultsFeedback(false);
    }
  };

  const userId = user?.primaryEmailAddress?.emailAddress || patient.email || user?.id || '';

  // Fix the consultation date once so it matches in /remedies/top and /patients/symptoms.
  const dateRef = useRef<string>(new Date().toISOString().slice(0, 10));
  const consultationDate = dateRef.current;

  // Track previous remedy to detect changes after recalculation.
  const previousRemedyRef = useRef<string | null>(null);

  const fetchDetails = useCallback(async (mainRemedyName: string, symptomsList: SymptomQuestionItem[]) => {
    setLoadingDetails(true);
    try {
      const res = await apiClient.post('/remedies/details', {
        remedy: mainRemedyName,
        validated_symptoms: symptomsList.map((s) => s.question || s.symptom),
        email: patient.email || undefined,
        date_0: consultationDate || undefined,
        consultation_token: patient.consultationToken || undefined,
      });
      setRemedyDetails(res.data);
    } catch (err) {
      console.error('Error fetching remedy details:', err);
    } finally {
      setLoadingDetails(false);
    }
  }, [patient.email, patient.consultationToken, consultationDate]);

  useEffect(() => {
    if (validated && results[0]?.remedy) {
      const activeSymptoms = validatedSymptoms.length ? validatedSymptoms : symptoms;
      void fetchDetails(results[0].remedy, activeSymptoms);
    }
  }, [validated, results, validatedSymptoms, symptoms, fetchDetails]);


  const fetchSymptoms = useCallback(async () => {
    if (!patient.email?.trim() || !patient.consultationToken?.trim()) {
      setToast({ type: 'error', message: 'Patient session is missing. Go to previous steps.' });
      return false;
    }

    setLoading(true);
    try {
      const recaptchaConfig = await getRequestConfig('preview_main_remedy');
      const response = await apiClient.post('/remedies/preview-main', {
        patient: {
          email: patient.email,
          name: patient.name,
          age: patient.age,
          gender: patient.gender,
          description_0: [],
          date_0: consultationDate,
          consultation_token: patient.consultationToken,
        },
      }, recaptchaConfig);

      const list: SymptomQuestionItem[] = Array.isArray(response.data?.items) ? response.data.items : [];
      setSymptoms(list);
      setSymptomsLoaded(true);
      return true;
    } catch (err: unknown) {
      const message = getErrorMessage(err, 'Error getting main remedy symptoms. Make sure you have completed the previous steps.');
      setErrorModal({ title: 'Error', message });
      return false;
    } finally {
      setLoading(false);
    }
  }, [consultationDate, getErrorMessage, getRequestConfig, patient, setErrorModal, setToast]);

  const fetchResults = useCallback(async () => {
    if (!patient.email?.trim() || !patient.consultationToken?.trim()) {
      setToast({ type: 'error', message: 'Patient session is missing. Go to previous steps.' });
      return false;
    }

    setLoading(true);
    try {
      const response = await apiClient.post('/remedies/top', {
        patient: {
          email: patient.email,
          name: patient.name,
          age: patient.age,
          gender: patient.gender,
          description_0: [],
          date_0: consultationDate,
          consultation_token: patient.consultationToken,
        },
        top_k: 5,
      });

      const data = response.data;
      const list: RemedyItem[] = data.remedies || (Array.isArray(data) ? data : []);
      const newTop = list[0]?.remedy ?? null;
      if (searched && newTop !== null && previousRemedyRef.current !== null) {
        if (newTop !== previousRemedyRef.current) {
          setToast({ type: 'success', message: `New main remedy: ${newTop}` });
        } else {
          setToast({ type: 'success', message: `Main remedy unchanged: ${newTop}` });
        }
      }
      previousRemedyRef.current = newTop;
      setResults(list);
      setSymptoms((current) => {
        const matchSet = new Set(list[0]?.matches ?? []);
        const filtered = current.filter((item) => matchSet.has(item.symptom));
        setValidatedSymptoms(filtered.length ? filtered : current);
        return filtered;
      });
      setSearched(true);
      await logTemporaryRemedies('Final Results', 6, patient, consultationDate);
      return true;
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err && (err as any).response?.data?.detail
          ? String((err as any).response.data.detail)
          : 'Error getting results. Make sure you have completed the previous steps.';
      setErrorModal({ title: 'Error', message });
      return false;
    } finally {
      setLoading(false);
    }
  }, [patient, consultationDate, setErrorModal, setToast, searched]);

  // Auto-load main remedy symptom preview
  useEffect(() => {
    if (patient.email && patient.consultationToken && !symptomsLoaded) {
      void fetchSymptoms();
    }
  }, [patient.email, patient.consultationToken, symptomsLoaded, fetchSymptoms]);

  // Check if consultation is already validated in backend when returning to Results view
  useEffect(() => {
    if (patient.email && patient.consultationToken) {
      apiClient
        .get('/patients/by-email', {
          params: {
            email: patient.email,
            name: patient.name,
            consultation_token: patient.consultationToken,
          },
        })
        .then(async (res) => {
          if (res.data) {
            setPatientStatus(res.data);
            if (res.data.validated && !validated) {
              setValidated(true);
              if (!symptomsLoaded) {
                await fetchSymptoms();
              }
              await fetchResults();
            }
          }
        })
        .catch((err) => console.error('Error checking consultation validation status:', err));
    }
  }, [patient.email, patient.consultationToken, patient.name, validated, symptomsLoaded, fetchSymptoms, fetchResults]);

  const validateConsultationInBackend = useCallback(async () => {
    if (patient.email && patient.consultationToken) {
      try {
        const recaptchaConfig = await getRequestConfig('validate_consultation');
        await apiClient.patch(
          '/patients/validate',
          {
            email: patient.email,
            date_0: consultationDate,
            consultation_token: patient.consultationToken,
            remedy_validated: previousRemedyRef.current || undefined,
          },
          recaptchaConfig
        );
      } catch (valErr) {
        console.error('Error sending backend validation:', valErr);
      }
    }
  }, [patient.email, patient.consultationToken, consultationDate, getRequestConfig]);

  // Handle return from payment gateway (unlocked=true)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('unlocked') === 'true' && !validated) {
      setValidated(true);
      const executeUnlock = async () => {
        if (!symptomsLoaded) {
          await fetchSymptoms();
        }
        const success = await fetchResults();
        if (success) {
          await validateConsultationInBackend();
        }
      };
      void executeUnlock();
    }
  }, [fetchResults, fetchSymptoms, symptomsLoaded, validated, validateConsultationInBackend]);


  const handleDeleteSymptom = useCallback(
    (symptom: string) => {
      if (validated) return;
      setConfirmModal({
        title: 'Delete symptom',
        message: `Delete the symptom "${symptom}" from the patient and recalculate the remedy?`,
        handleConfirm: async () => {
          setDeletingSymptom(symptom);
          try {
            await deletePatientSymptom({
              email: patient.email,
              name: patient.name,
              date_0: consultationDate,
              consultation_token: patient.consultationToken,
              symptom_description: symptom,
            });
            setToast({ type: 'success', message: 'Symptom deleted. Recalculating main remedy symptoms...' });
            setResults([]);
            setSearched(false);
            await fetchSymptoms();
          } catch (err: unknown) {
            const message =
              err && typeof err === 'object' && 'response' in err && (err as any).response?.data?.detail
                ? String((err as any).response.data.detail)
                : 'Could not delete the symptom.';
            setErrorModal({ title: 'Error', message });
          } finally {
            setDeletingSymptom(null);
          }
        },
      });
    },
    [validated, patient, consultationDate, fetchSymptoms, setConfirmModal, setErrorModal, setToast],
  );

  const handleValidate = useCallback(async () => {
    if (!symptoms.length) return;
    setValidatedSymptoms([...symptoms]);

    setLoading(true);
    let credits = 0;
    try {
      credits = userId ? await getUserCredits(userId) : 0;
    } catch (err) {
      console.error('Error fetching credits:', err);
    } finally {
      setLoading(false);
    }

    if (credits >= 1) {
      setConfirmModal({
        title: 'Use Account Credit',
        message: `You have ${credits} available credit(s). 1 credit will be used to unlock the remedy for this consultation. Do you wish to continue?`,
        handleConfirm: async () => {
          setLoading(true);
          try {
            const res = await consumeUserCredit(userId);
            if (res.success) {
              await validateConsultationInBackend();
              setToast({ type: 'success', message: 'Credit applied. Revealing remedy...' });
              setValidated(true);
              await fetchResults();
            } else {
              setToast({ type: 'error', message: res.reason || 'Error using credit.' });
            }
          } catch (_err: unknown) {
            setErrorModal({ title: 'Error', message: 'Could not apply credit.' });
          } finally {
            setLoading(false);
          }
        },
      });
    } else {
      setConfirmModal({
        title: 'Validate Symptoms & Payment',
        message: 'You have no available credits. You will be redirected to the payment gateway to choose an option (Bundle or Single Consultation) and unlock the remedy. Continue?',
        handleConfirm: () => {
          if (patient.consultationToken) {
            localStorage.setItem('pending_payment_consultation_token', patient.consultationToken);
          }
          navigate('/payment');
        },
      });
    }
  }, [symptoms, userId, setConfirmModal, consumeUserCredit, validateConsultationInBackend, setToast, fetchResults, setErrorModal, patient.consultationToken, navigate]);



  const handleExit = useCallback(() => {
    navigate('/account');
  }, [navigate]);

  const handleRecalculate = useCallback(() => {
    if (validated) return;
    setResults([]);
    setSearched(false);
    void fetchSymptoms();
  }, [validated, fetchSymptoms]);

  return (
    <div className="p-4 mx-auto max-w-7xl">
      <ConsultationStepper />

      {validated && (
        <div className="p-3.5 mb-4 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl font-medium text-sm flex items-center justify-between shadow-xs">
          <div className="flex items-center gap-2">
            <span className="font-bold">🔒 Validated & Paid Consultation</span>
            <span className="text-xs text-emerald-700">(Read-Only Mode)</span>
          </div>
          <span className="text-xs font-bold uppercase tracking-wider bg-emerald-200 text-emerald-900 px-2.5 py-1 rounded-md">
            Completed
          </span>
        </div>
      )}

      <div className="mb-4">
        <Typography variant="h6" className="text-gray-800 mb-1 font-bold">
          Step 6: Analysis Result
        </Typography>
        <Typography variant="body2" className="text-gray-600">
          {validated
            ? 'This consultation is validated and completed. You are viewing the prescribed remedy and administration instructions in Read-Only mode.'
            : 'Review only the symptoms associated with the current main remedy. If you remove one, the main remedy is recalculated and this list updates before validation.'}
        </Typography>
      </div>

      <Box className="mb-6 flex justify-end gap-2">
        {!validated && (
          <Button onClick={handleRecalculate} disabled={loading} variant="outlined">
            {loading ? 'Refreshing...' : 'Recalculate main remedy'}
          </Button>
        )}
        {!validated && (
          <Button
            onClick={handleValidate}
            disabled={loading || !symptoms.length}
            variant="contained"
            color="primary"
          >
            Validate symptoms
          </Button>
        )}
        {validated && (
          <Button onClick={handleExit} variant="outlined" color="secondary">
            Finish consultation
          </Button>
        )}
      </Box>

      {loading && (
        <Typography className="text-center mt-8 text-gray-600">
          {validated ? 'Calculating remedy...' : 'Loading main remedy symptoms...'}
        </Typography>
      )}

      {!loading && symptomsLoaded && !symptoms.length && (
        <Typography className="text-center mt-8 text-gray-500">
          No symptoms found for the current main remedy. Try adding more symptoms in previous steps.
        </Typography>
      )}

      {!loading && symptoms.length > 0 && (
        <div className="grid gap-4 md:grid-cols-1 max-w-3xl mx-auto">
          {!validated && (
            <Card className="border border-gray-200 shadow-sm">
              <CardContent>
                <div className="flex justify-between items-start">
                  <div>
                    <Typography variant="h6" className="font-semibold text-gray-500">
                      Remedy hidden until symptom validation
                    </Typography>
                  </div>
                  <Chip
                    label="Symptoms pending"
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                </div>

                <Divider className="my-2" />

                <Typography variant="body2" className="font-semibold text-gray-700 mb-1">
                  Main remedy symptoms ({symptoms.length}):
                </Typography>
                <List dense disablePadding>
                  {symptoms.map(({ symptom, question }, sIdx) => (
                    <ListItem key={`${symptom}-${sIdx}`} disableGutters className="pl-2 pr-12">
                      <ListItemText
                        primary={question}
                        primaryTypographyProps={{ variant: 'body2', className: 'text-gray-700' }}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Delete symptom from patient and recalculate">
                          <span>
                            <IconButton
                              edge="end"
                              size="small"
                              color="error"
                              disabled={deletingSymptom === symptom}
                              onClick={() => handleDeleteSymptom(symptom)}
                              aria-label={`Delete symptom ${symptom}`}
                            >
                              ✕
                            </IconButton>
                          </span>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}

          {validated && results[0] && (
            <div className="space-y-4">
              {/* Primary Remedy Card */}
              <Card className="border border-emerald-200 bg-emerald-50/10 shadow-sm">
                <CardContent>
                  <div className="flex justify-between items-start flex-wrap gap-2">
                    <div>
                      <Typography variant="h5" className="font-bold text-emerald-900 flex items-center flex-wrap gap-2">
                        <span>{remedyDetails?.full_name || results[0].remedy}</span>
                        {remedyDetails?.full_name && remedyDetails.full_name !== results[0].remedy && (
                          <span className="text-base sm:text-lg font-normal text-emerald-700">
                            ({results[0].remedy})
                          </span>
                        )}
                      </Typography>
                    </div>
                    <div className="flex items-center gap-2">
                      {remedyDetails?.diagnosed_potency && (
                        <Chip
                          label={`Potency: ${remedyDetails.diagnosed_potency}`}
                          color="primary"
                          size="small"
                          variant="filled"
                          className="font-bold"
                        />
                      )}
                      <Chip
                        label="1st Prescribed Remedy"
                        color="success"
                        size="small"
                        variant="filled"
                      />
                    </div>
                  </div>

                  <Divider className="my-3" />

                  {loadingDetails && (
                    <div className="p-4 bg-white/90 rounded-lg border border-emerald-200 text-center my-3 shadow-xs">
                      <Typography variant="body2" className="text-emerald-700 font-medium animate-pulse">
                        Generating personalized remedy analysis with AI...
                      </Typography>
                    </div>
                  )}

                  {remedyDetails && !loadingDetails && (
                    <div className="space-y-4 mt-4 pt-3 border-t border-emerald-100">
                      {remedyDetails.tailored_description && (
                        <div className="bg-white p-4 rounded-lg border border-emerald-200 shadow-2xs">
                          <Typography variant="subtitle2" className="font-bold text-emerald-800 mb-1.5 flex items-center gap-1.5">
                            📋 Remedy Information Tailored to Your Symptoms
                          </Typography>
                          <Typography variant="body2" className="text-gray-700 whitespace-pre-line leading-relaxed">
                            {remedyDetails.tailored_description}
                          </Typography>
                        </div>
                      )}

                      {remedyDetails.administration_instructions && (
                        <div className="bg-blue-50/60 p-4 rounded-lg border border-blue-200 shadow-2xs">
                          <Typography variant="subtitle2" className="font-bold text-blue-900 mb-1.5 flex items-center gap-1.5">
                            💧 Administration Instructions (Plus Method - 3 Days)
                          </Typography>
                          <Typography variant="body2" className="text-gray-700 whitespace-pre-line leading-relaxed">
                            {remedyDetails.administration_instructions}
                          </Typography>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Secondary Remedy Card */}
              <Card className={`border shadow-sm ${patientStatus?.remedy_2_is_available ? 'border-blue-200 bg-blue-50/10' : 'border-amber-200 bg-amber-50/10'}`}>
                <CardContent>
                  <div className="flex justify-between items-start flex-wrap gap-2">
                    <div>
                      <Typography variant="h6" className="font-bold text-slate-800 flex items-center flex-wrap gap-2">
                        <span>
                          {patientStatus?.remedy_2_is_available
                            ? (patientStatus?.remedy_2_full_name || patientStatus?.remedy_2_short_name || results[1]?.remedy || '2nd Prescribed Remedy')
                            : '2nd Prescribed Remedy'}
                        </span>
                        {patientStatus?.remedy_2_is_available && patientStatus?.remedy_2_short_name && patientStatus?.remedy_2_full_name !== patientStatus?.remedy_2_short_name && (
                          <span className="text-sm font-normal text-slate-600">
                            ({patientStatus.remedy_2_short_name})
                          </span>
                        )}
                      </Typography>
                      {patientStatus?.remedy_2_available_at && (
                        <Typography variant="caption" className="text-slate-500 font-medium block mt-0.5">
                          Availability date: {patientStatus.remedy_2_available_at.slice(0, 10)} ({(() => {
                            const availDate = new Date(patientStatus.remedy_2_available_at.replace(' ', 'T'));
                            const diff = Math.max(0, Math.ceil((availDate.getTime() - new Date().getTime()) / 86400000));
                            return `To be released in ${diff} ${diff === 1 ? 'day' : 'days'}`;
                          })()})
                        </Typography>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {patientStatus?.remedy_2_is_available && (
                        <Chip
                          label={`Potency: ${patientStatus.remedy_2_potency || '30CH'}`}
                          color="primary"
                          size="small"
                          variant="filled"
                          className="font-bold"
                        />
                      )}
                      <Chip
                        label={
                          patientStatus?.remedy_2_is_available
                            ? "2nd Prescribed Remedy"
                            : `🔒 ${(() => {
                                const availDate = new Date(patientStatus?.remedy_2_available_at ? patientStatus.remedy_2_available_at.replace(' ', 'T') : new Date());
                                const diff = Math.max(0, Math.ceil((availDate.getTime() - new Date().getTime()) / 86400000));
                                return `To be released in ${diff} ${diff === 1 ? 'day' : 'days'}`;
                              })()}`
                        }
                        color={patientStatus?.remedy_2_is_available ? "primary" : "warning"}
                        size="small"
                        variant="filled"
                      />
                    </div>
                  </div>

                  <Divider className="my-3" />

                  {patientStatus?.remedy_2_is_available ? (
                    <div className="space-y-4 mt-4">
                      {patientStatus.tailored_description_2 && (
                        <div className="bg-white p-4 rounded-lg border border-blue-200 shadow-2xs">
                          <Typography variant="subtitle2" className="font-bold text-blue-900 mb-1.5 flex items-center gap-1.5">
                            📋 2nd Remedy Information Tailored to Your Symptoms
                          </Typography>
                          <Typography variant="body2" className="text-gray-700 whitespace-pre-line leading-relaxed">
                            {patientStatus.tailored_description_2}
                          </Typography>
                        </div>
                      )}

                      {patientStatus.administration_instructions_2 && (
                        <div className="bg-blue-50/60 p-4 rounded-lg border border-blue-200 shadow-2xs">
                          <Typography variant="subtitle2" className="font-bold text-blue-900 mb-1.5 flex items-center gap-1.5">
                            💧 2nd Remedy Administration Instructions
                          </Typography>
                          <Typography variant="body2" className="text-gray-700 whitespace-pre-line leading-relaxed">
                            {patientStatus.administration_instructions_2}
                          </Typography>
                        </div>
                      )}

                      {patientStatus.primary_remedy_feedback && (
                        <div className="bg-white p-3 rounded-lg border border-blue-200 shadow-2xs text-xs text-blue-950">
                          <Typography variant="subtitle2" className="font-bold text-blue-900 mb-1">
                            💬 Your Evolution Feedback ({patientStatus.primary_remedy_feedback_date?.slice(0, 10)}):
                          </Typography>
                          <Typography variant="body2" className="italic text-gray-700">
                            "{patientStatus.primary_remedy_feedback}"
                          </Typography>
                        </div>
                      )}
                    </div>
                  ) : patientStatus?.remedy_2_requires_feedback ? (
                    <div className="p-4 bg-amber-100/80 rounded-lg border border-amber-300 text-amber-950 space-y-2.5 mt-2">
                      <Typography variant="subtitle2" className="font-bold text-amber-950 flex items-center gap-1.5">
                        💬 Feedback Required to Unlock 2nd Remedy
                      </Typography>
                      <Typography variant="body2" className="text-amber-900 text-xs">
                        Please provide feedback on your progress and evolution after taking the 1st remedy to unlock the 2nd prescribed remedy:
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        placeholder="Describe how symptoms evolved, any changes or progress observed after the 1st remedy..."
                        value={resultsFeedback}
                        onChange={(e) => setResultsFeedback(e.target.value)}
                        className="bg-white rounded text-xs"
                      />
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        disabled={submittingResultsFeedback || !resultsFeedback.trim()}
                        onClick={handleResultsFeedbackSubmit}
                        className="normal-case text-xs font-bold"
                      >
                        {submittingResultsFeedback ? 'Submitting...' : 'Submit Feedback & Unlock 2nd Remedy'}
                      </Button>
                    </div>
                  ) : (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 text-amber-900 text-sm space-y-1">
                      <Typography variant="subtitle2" className="font-bold text-amber-900 flex items-center gap-1.5">
                        🔒 Second Remedy Pending Release
                      </Typography>
                      <Typography variant="body2" className="text-amber-800">
                        Following primary remedy validation, the 2nd prescribed remedy (along with its detailed explanation and potency) will automatically become available to you ({(() => {
                          const availDate = new Date(patientStatus?.remedy_2_available_at ? patientStatus.remedy_2_available_at.replace(' ', 'T') : new Date());
                          const diff = Math.max(0, Math.ceil((availDate.getTime() - new Date().getTime()) / 86400000));
                          return `To be released in ${diff} ${diff === 1 ? 'day' : 'days'}`;
                        })()} on {patientStatus?.remedy_2_available_at ? patientStatus.remedy_2_available_at.slice(0, 10) : 'scheduled date'}).
                      </Typography>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

